import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, ErrorNotice, Field, TextInput } from '@bcecloud/portal-ui';
import { AuthCard } from '@/components/AuthCard';
import { PasswordInput } from '@/components/PasswordInput';
import { register } from '@/lib/auth';

/**
 * Registration page for new client accounts.
 */
export function RegisterPage(): React.ReactElement {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  /**
   * Updates one field, clearing any previous error.
   *
   * @param field - Form field name.
   * @param value - New value.
   */
  function updateField(field: string, value: string): void {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  }

  /**
   * Registers and lands on the events list.
   *
   * @param event - The submit event.
   */
  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(form);
      navigate('/');
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { message?: string; code?: number } } };
        if (axiosErr.response?.data?.code === 11000) {
          setError('An account with that email already exists.');
        } else {
          setError(axiosErr.response?.data?.message || 'Registration failed. Please try again.');
        }
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthCard title="Create account" subtitle="Sign up for My Events">
      <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4" noValidate>
        {error ? <ErrorNotice inline message={error} /> : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" htmlFor="firstName">
            <TextInput
              id="firstName"
              autoComplete="given-name"
              required
              value={form.firstName}
              onChange={(event) => updateField('firstName', event.target.value)}
            />
          </Field>
          <Field label="Last name" htmlFor="lastName">
            <TextInput
              id="lastName"
              autoComplete="family-name"
              required
              value={form.lastName}
              onChange={(event) => updateField('lastName', event.target.value)}
            />
          </Field>
        </div>

        <Field label="Email" htmlFor="email">
          <TextInput
            id="email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(event) => updateField('email', event.target.value)}
          />
        </Field>

        <Field label="Company" htmlFor="company">
          <TextInput
            id="company"
            autoComplete="organization"
            value={form.company}
            onChange={(event) => updateField('company', event.target.value)}
          />
        </Field>

        <Field label="Password" htmlFor="password">
          <PasswordInput
            id="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={form.password}
            onChange={(event) => updateField('password', event.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" disabled={loading} className="w-full">
          {loading ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <p className="mt-5 text-center text-portal-small text-portal-muted">
        Already have an account?{' '}
        <Link
          to="/auth/login"
          className="font-bold text-brand-link underline-offset-2 hover:underline"
        >
          Sign in
        </Link>
      </p>
    </AuthCard>
  );
}
